'use client';

import { useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';

import Icon from '@/components/Icon/Icon';
import { buttonVariants } from '@/components/ui/button';
import { useFormatDate } from '@/hooks/date';
import { cn } from '@/lib/cn';
import { getCurrentLocale } from '@/lib/i18n';

type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  showYearMonthSelect?: boolean; // 年月選択UIを表示するかどうか
  defaultMonth?: Date; // 初期表示月を指定するプロパティ
};

/**
 * デフォルトのスタイル設定(shadcnのデフォルト定義)
 * Tailwind CSSのクラス名を使用してカレンダーの各部分のスタイルを定義
 */
const YEAR_DROPDOWN_PAST_RANGE = 120;
const YEAR_DROPDOWN_FUTURE_RANGE = 50;

const defaultClassNames = {
  months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
  month: 'space-y-4',
  caption: 'flex justify-center px-10 pt-1 relative items-center',
  nav: 'space-x-1 flex items-center',
  nav_button: cn(
    buttonVariants({ variant: 'neutral' }),
    'h-7 w-7 p-0 text-neutral-on-fill',
  ),
  nav_button_previous: 'absolute left-1',
  nav_button_next: 'absolute right-1',
  table: 'mx-auto border-collapse space-y-1',
  head_row: 'flex',
  head_cell: 'text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]',
  row: 'flex w-full mt-2',
  day: cn(
    buttonVariants({ variant: 'ghost' }),
    'h-8 w-8 p-0 font-normal aria-selected:opacity-100',
  ),
  day_range_start: 'day-range-start',
  day_range_end: 'day-range-end',
  day_selected:
    'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
  day_today: 'bg-primary-muted-fill text-primary-on-muted-fill',
  day_outside:
    'day-outside text-muted-foreground opacity-50 aria-selected:bg-primary-muted-fill aria-selected:text-muted-foreground aria-selected:opacity-30',
  day_disabled: 'text-muted-foreground opacity-50',
  day_range_middle:
    'aria-selected:bg-primary-muted-fill aria-selected:text-primary-on-muted-fill',
  day_hidden: 'invisible',
  vhidden: 'hidden',
  caption_dropdowns: 'flex items-center gap-2',
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  showYearMonthSelect = false,
  defaultMonth,
  fromYear,
  toYear,
  fromDate,
  toDate,
  ...props
}: CalendarProps) {
  const locale = getCurrentLocale();
  const formatDate = useFormatDate();
  const [currentMonth, setCurrentMonth] = useState<Date>(
    defaultMonth || props.month || new Date(),
  );

  useEffect(() => {
    if (props.month) {
      // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- TODO: eslint-plugin-react-hooks v7 の React Compiler ルールに対応
      setCurrentMonth(props.month);
    }
  }, [props.month]);

  const handleMonthChange = (date: Date) => {
    setCurrentMonth(date);
    props.onMonthChange?.(date);
  };

  const dropdownTriggerClassName =
    'h-7 relative inline-flex items-center rounded-md border border-neutral-muted-border bg-neutral-fill hover:bg-primary-muted-fill shadow-xs ring-offset-neutral-fill focus-within:outline-hidden focus-within:ring-1 focus-within:ring-neutral-muted-border';

  const captionLayout = showYearMonthSelect ? 'dropdown-buttons' : 'buttons';
  const currentYear = new Date().getFullYear();

  return (
    <DayPicker
      captionLayout={captionLayout}
      fromYear={
        fromYear ??
        (showYearMonthSelect && !fromDate
          ? currentYear - YEAR_DROPDOWN_PAST_RANGE
          : undefined)
      }
      toYear={
        toYear ??
        (showYearMonthSelect && !toDate
          ? currentYear + YEAR_DROPDOWN_FUTURE_RANGE
          : undefined)
      }
      month={currentMonth}
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      onMonthChange={handleMonthChange}
      classNames={{
        ...defaultClassNames,
        dropdown_month: dropdownTriggerClassName,
        dropdown_year: dropdownTriggerClassName,
        dropdown: 'absolute inset-0 z-10 cursor-pointer opacity-0',
        caption_label: cn(
          'text-sm font-medium',
          showYearMonthSelect &&
            'flex items-center gap-1 px-2 py-1 text-label font-semibold',
        ),
        cell: cn(
          'relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-primary-muted-fill [&:has([aria-selected].day-outside)]:bg-primary-muted-fill [&:has([aria-selected].day-range-end)]:rounded-r-md',
          props.mode === 'range'
            ? '[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md'
            : '[&:has([aria-selected])]:rounded-md',
        ),
        ...classNames,
      }}
      components={{
        IconLeft: () => (
          <Icon iconName="ChevronLeft" className="text-neutral-on-fill" />
        ),
        IconRight: () => (
          <Icon iconName="ChevronRight" className="text-neutral-on-fill" />
        ),
        IconDropdown: () => (
          <Icon iconName="ChevronDownIcon" className="shrink-0" />
        ),
      }}
      formatters={{
        formatCaption: (date) => formatDate(date, { preset: 'yearMonth' }),
      }}
      fromDate={fromDate}
      toDate={toDate}
      locale={locale}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
